import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ClientService } from './client.service';
import { AdaptiveFlashcardModality } from '@core/models/adaptive-flashcard-modality.enum';

export { AdaptiveFlashcardModality } from '@core/models/adaptive-flashcard-modality.enum';

export enum ReviewSourceType {
  Train = 1,
  ReviewDue = 2,
  QuickPractice = 3,
}

export enum StudySessionType {
  ScheduledReview = 1,
  QuickPractice = 2,
}

export enum ReviewGradeType {
  None = 0,
  Again = 1,
  Hard = 2,
  Good = 3,
  Easy = 4,
}

export type StudySessionModel = {
  id?: number;
  userId?: number;
  workspaceId?: number;
  deckId?: number | null;
  sessionType?: StudySessionType;
  startedAt?: string;
  endedAt?: string | null;
  activeSeconds?: number;
  cardsReviewedCount?: number;
  createdAt?: string;
};

export type FlashcardUserProgressModel = {
  id?: number;
  flashcardId?: number;
  dueAt?: string | null;
  lastReviewedAt?: string | null;
  stability?: number;
  difficulty?: number;
  retrievability?: number;
  scheduledDays?: number;
  elapsedDays?: number;
  reviewCount?: number;
  lapseCount?: number;
  lastReviewGrade?: number;
};

export type FlashcardReviewLogModel = {
  id?: number;
  studySessionId?: number | null;
  deckId?: number;
  flashcardId?: number;
  startedAt?: string;
  answeredAt?: string;
  activeSeconds?: number;
  wasCorrect?: boolean | null;
  answerText?: string | null;
  evaluationText?: string | null;
  evaluationSource?: number;
  evaluationStatus?: number;
  reviewSource?: ReviewSourceType;
  reviewGrade?: ReviewGradeType;
  adaptiveModality?: AdaptiveFlashcardModality | null;
};

export type StudySessionCreatePayload = {
  deckId: number;
  sessionType?: StudySessionType;
};

export type StartReviewRequest = {
  deckId: number;
  flashcardId: number;
  reviewSource: ReviewSourceType;
  adaptiveModality?: AdaptiveFlashcardModality;
  adaptivePresentationFlashcardId?: number;
};

export type ReviewFlashcardRequest = {
  reviewLogId?: number;
  deckId: number;
  flashcardId: number;
  adaptiveModality?: AdaptiveFlashcardModality;
  wasCorrect: boolean | null;
  answerText?: string;
  reviewSource: ReviewSourceType;
  reviewGrade: ReviewGradeType;
  shortTalkMessages?: ShortTalkReviewMessage[];
};

export type ShortTalkReviewMessage = {
  role: 'bot' | 'learner';
  text: string;
};

export type ReviewFlashcardResponse = {
  reviewLog: FlashcardReviewLogModel;
  progress: FlashcardUserProgressModel;
  studySession: StudySessionModel;
};

type ReviewFlashcardApiResponse = {
  reviewLog?: FlashcardReviewLogModel;
  progress?: FlashcardUserProgressModel;
  studySession?: StudySessionModel;
};

export type StudySessionFinishPayload = {
  cardsReviewedCount: number;
};

type ItemEnvelope<T> =
  | T
  | {
      data?: T;
      item?: T;
      value?: T;
    };

@Injectable({ providedIn: 'root' })
export class StudySessionService {
  private readonly client = inject(ClientService);

  create(payload: StudySessionCreatePayload): Observable<StudySessionModel> {
    return this.client
      .post<
        ItemEnvelope<StudySessionModel>,
        StudySessionCreatePayload
      >('study-session', payload)
      .pipe(
        map((response) =>
          this.normalizeStudySession(this.unwrapItem(response)),
        ),
      );
  }

  startReview(
    sessionId: number,
    payload: StartReviewRequest,
  ): Observable<FlashcardReviewLogModel> {
    return this.client
      .post<
        ItemEnvelope<FlashcardReviewLogModel>,
        StartReviewRequest
      >(`study-session/${sessionId}/start-review`, payload)
      .pipe(
        map((response) =>
          this.normalizeFlashcardReviewLog(this.unwrapItem(response)),
        ),
      );
  }

  review(
    sessionId: number,
    payload: ReviewFlashcardRequest,
  ): Observable<ReviewFlashcardResponse> {
    return this.client
      .post<
        ItemEnvelope<ReviewFlashcardApiResponse>,
        ReviewFlashcardRequest
      >(`study-session/${sessionId}/review`, payload)
      .pipe(map((response) => this.unwrapReview(response)));
  }

  finish(
    sessionId: number,
    payload: StudySessionFinishPayload,
  ): Observable<StudySessionModel> {
    return this.client
      .post<
        ItemEnvelope<StudySessionModel>,
        StudySessionFinishPayload
      >(`study-session/${sessionId}/finish`, payload)
      .pipe(
        map((response) =>
          this.normalizeStudySession(this.unwrapItem(response)),
        ),
      );
  }

  private unwrapItem<T>(response: ItemEnvelope<T>): T {
    const wrapped = response as Record<string, unknown>;
    return (
      (wrapped['data'] as T | undefined) ??
      (wrapped['item'] as T | undefined) ??
      (wrapped['value'] as T | undefined) ??
      (response as T)
    );
  }

  private unwrapReview(
    response: ItemEnvelope<ReviewFlashcardApiResponse>,
  ): ReviewFlashcardResponse {
    const value = this.unwrapItem(response) as Record<string, unknown>;
    const reviewLog = (value['reviewLog'] ?? {}) as FlashcardReviewLogModel;
    const progress = (value['progress'] ?? {}) as FlashcardUserProgressModel;
    const studySession = (value['studySession'] ?? {}) as StudySessionModel;

    return {
      reviewLog: this.normalizeFlashcardReviewLog(reviewLog),
      progress: this.normalizeFlashcardUserProgress(progress),
      studySession: this.normalizeStudySession(studySession),
    };
  }

  private normalizeStudySession(model: StudySessionModel): StudySessionModel {
    return {
      id: this.toNumber(model.id),
      userId: this.toNumber(model.userId),
      workspaceId: this.toNumber(model.workspaceId),
      deckId: this.toNullableNumber(model.deckId),
      sessionType: this.toSessionType(model.sessionType),
      startedAt: this.toString(model.startedAt),
      endedAt: this.toNullableString(model.endedAt),
      activeSeconds: this.toNumber(model.activeSeconds),
      cardsReviewedCount: this.toNumber(model.cardsReviewedCount),
      createdAt: this.toString(model.createdAt),
    };
  }

  private normalizeFlashcardUserProgress(
    model: FlashcardUserProgressModel,
  ): FlashcardUserProgressModel {
    return {
      id: this.toNumber(model.id),
      flashcardId: this.toNumber(model.flashcardId),
      dueAt: this.toNullableString(model.dueAt),
      lastReviewedAt: this.toNullableString(model.lastReviewedAt),
      stability: this.toNumber(model.stability),
      difficulty: this.toNumber(model.difficulty),
      retrievability: this.toNumber(model.retrievability),
      scheduledDays: this.toNumber(model.scheduledDays),
      elapsedDays: this.toNumber(model.elapsedDays),
      reviewCount: this.toNumber(model.reviewCount),
      lapseCount: this.toNumber(model.lapseCount),
      lastReviewGrade: this.toNumber(model.lastReviewGrade),
    };
  }

  private normalizeFlashcardReviewLog(
    model: FlashcardReviewLogModel,
  ): FlashcardReviewLogModel {
    return {
      id: this.toNumber(model.id),
      studySessionId: this.toNullableNumber(model.studySessionId),
      deckId: this.toNumber(model.deckId),
      flashcardId: this.toNumber(model.flashcardId),
      startedAt: this.toString(model.startedAt),
      answeredAt: this.toString(model.answeredAt),
      activeSeconds: this.toNumber(model.activeSeconds),
      wasCorrect: this.toNullableBoolean(model.wasCorrect),
      answerText: this.toNullableString(model.answerText),
      evaluationText: this.toNullableString(model.evaluationText),
      evaluationSource: this.toNumber(model.evaluationSource),
      evaluationStatus: this.toNumber(model.evaluationStatus),
      reviewSource: this.toReviewSource(model.reviewSource),
      reviewGrade: this.toReviewGrade(model.reviewGrade),
      adaptiveModality: this.toAdaptiveModality(model.adaptiveModality),
    };
  }

  private toNumber(value: number | null | undefined, fallback = 0): number {
    const parsed = Number(value ?? fallback);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private toNullableNumber(value: number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toAdaptiveModality(
    value: AdaptiveFlashcardModality | null | undefined,
  ): AdaptiveFlashcardModality | null {
    const numericValue = this.toNullableNumber(value);
    return numericValue !== null &&
      numericValue >= AdaptiveFlashcardModality.RecognitionMultipleChoice &&
      numericValue <= AdaptiveFlashcardModality.AppliedAudioConversation
      ? (numericValue as AdaptiveFlashcardModality)
      : null;
  }

  private toString(value: string | null | undefined): string {
    return String(value ?? '');
  }

  private toNullableString(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
  }

  private toNullableBoolean(value: boolean | null | undefined): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    return null;
  }

  private toReviewSource(
    value: ReviewSourceType | null | undefined,
  ): ReviewSourceType {
    return this.toNumber(value, ReviewSourceType.Train) as ReviewSourceType;
  }

  private toSessionType(
    value: StudySessionType | null | undefined,
  ): StudySessionType {
    return this.toNumber(value, StudySessionType.ScheduledReview) ===
      StudySessionType.QuickPractice
      ? StudySessionType.QuickPractice
      : StudySessionType.ScheduledReview;
  }

  private toReviewGrade(
    value: ReviewGradeType | null | undefined,
  ): ReviewGradeType {
    return this.toNumber(value, ReviewGradeType.None) as ReviewGradeType;
  }
}
